/**
 * Qual painel a pessoa vê, e qual abre por padrão.
 *
 * MÓDULO PURO, SEM IMPORT — e isso é o ponto, não um detalhe de organização.
 *
 * A regra morava dentro de `use-active-panel`, que importa o hook do cadastro
 * de anunciante, que importa o cliente do Supabase. Um teste da regra passava a
 * arrastar o cliente inteiro, e o CI reprovou com `supabaseUrl is required`:
 * aqui há `.env`, lá não. Localmente o teste media a regra; no CI ele nem
 * carregava.
 *
 * É o mesmo desenho de `trigger-engine/rules.ts`, e pela mesma razão: regra de
 * negócio que se testa sem subir nada.
 */

export type PanelType = "user" | "partner" | "admin" | "superadmin";

/**
 * Qual painel abrir para quem tem mais de um.
 *
 * Era "o último da lista", o que funcionava enquanto a lista subia em poder:
 * user → admin → superadmin. Com o painel do anunciante isso deixa de valer —
 * ele não é um degrau de poder, é um CONTEXTO DE USO —, e o "último" mandaria
 * um superadmin anunciante abrir na Vitrine.
 *
 * A regra passa a ser dita em voz alta:
 *
 *   1. quem administra abre administrando — é o que a pessoa entrou para fazer;
 *   2. quem é anunciante e NÃO tem imóvel abre no painel de anunciante: o painel
 *      Cliente lhe ofereceria 19 itens de menu dos quais ela usa dois;
 *   3. todo o resto abre no Cliente.
 *
 * A regra 2 é medida, não perguntada: ninguém precisa declarar "sou só
 * anunciante". E ela se desfaz sozinha — no dia em que essa pessoa cadastrar um
 * imóvel, o Cliente volta a ser o padrão, sem suporte e sem migração de conta.
 */
export function painelPadrao(
  disponiveis: PanelType[],
  opcoes: { temImoveis: boolean },
): PanelType {
  if (disponiveis.includes("superadmin")) return "superadmin";
  if (disponiveis.includes("admin")) return "admin";
  if (disponiveis.includes("partner") && !opcoes.temImoveis) return "partner";
  return "user";
}

/**
 * Os painéis que esta conta alcança.
 *
 * O painel do anunciante NÃO vem do papel: vem de ter cadastro aprovado. O papel
 * `partner` existe no enum e o banco o recusa, por um gatilho de hierarquia de
 * 20/04 — ser anunciante é ter a linha em `service_partners`, e nada mais.
 */
export function painelsDisponiveis(
  role: string | null | undefined,
  isPartner: boolean,
): PanelType[] {
  const base: PanelType[] = ["user"];
  if (isPartner) base.push("partner");
  if (role === "superadmin") return [...base, "admin", "superadmin"];
  if (role === "admin") return [...base, "admin"];
  return base;
}
